// Picking the empty a driver will take back, by pointing at it.
//
// Empty # was a text box. With 410 Containers in the table nobody can hold a
// container number in their head while they scroll for the row it belongs to, so
// it got typed from memory and mistyped - and a mistyped number matches nothing,
// which means the driver handoff silently does not happen (Alan, 2026-08-03).
//
// So the cell becomes a pointer. Clicking it narrows the table to the Containers
// that are actually available to be taken back - the ones the customer has
// reported empty - and clicking one of those pastes its number back into the cell
// that started it. The backend does the rest: buildEmptyContainerHandoffPlan makes
// this task's pickup driver the empty's return driver and stamps the pickup
// appointment as its actual empty return.
//
// The table already knows how to narrow itself to a set of row ids for the Work
// Pool. This reuses that, so the query path stays untouched.

const REPORTED_EMPTY_STATUS = 'empty_return_reported';

function oceanStatusOf(row) {
  const value = row?.ocean_status ?? row?.status;
  return typeof value === 'string' ? value : '';
}

export function containerNumberOf(row) {
  const value = row?.container_no;
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The Containers this cell may point at: reported empty, numbered, and not the
 * row doing the pointing. A Container cannot be its own empty.
 */
export function reportedEmptyCandidates(rows, sourceRow) {
  if (!Array.isArray(rows)) return [];
  const sourceId = sourceRow?.id ?? null;
  return rows.filter((row) => (
    row?.id !== sourceId
    && oceanStatusOf(row) === REPORTED_EMPTY_STATUS
    && containerNumberOf(row) !== ''
  ));
}

/**
 * Fail-closed: with nothing reported empty there is nothing to point at, and
 * narrowing the table to zero rows would look like the data had vanished.
 */
export function createEmptyContainerPickMode() {
  let active = null;

  return Object.freeze({
    get sourceContainerId() { return active?.sourceContainerId ?? null; },
    isActive() { return active !== null; },
    begin({ sourceRow, rows }) {
      const candidates = reportedEmptyCandidates(rows, sourceRow);
      if (!sourceRow?.id || candidates.length === 0) return null;
      active = {
        sourceContainerId: sourceRow.id,
        candidateIds: candidates.map(({ id }) => id),
      };
      return { ...active, candidates };
    },
    // Returns what to write and where, or null when the click was not on a
    // candidate - a click on the source row's own cell, say, or on a row that
    // scrolled in after the mode opened.
    pick(row) {
      if (!active || !row || !active.candidateIds.includes(row.id)) return null;
      const containerNo = containerNumberOf(row);
      if (!containerNo) return null;
      const picked = { containerId: active.sourceContainerId, value: containerNo };
      active = null;
      return picked;
    },
    cancel() {
      const wasActive = active !== null;
      active = null;
      return wasActive;
    },
  });
}

// The other half of picking across the split (Alan, 2026-09-30: "点选的时候是可以鼠标在右边
// 点击任何空柜可以直接选上"): the table that holds the empties is asked to be
// pointed at, and says so. Same classes and the same Cancel as the local pick.
export function externalEmptyPickBannerMarkup() {
  return `<div class="dashboard-empty-pick-banner" role="status" data-empty-pick-banner>
      <span class="dashboard-empty-pick-banner__text">点任何一个空柜就选上 &mdash; Click any empty to use its number.</span>
      <button type="button" class="dashboard-empty-pick-banner__cancel" data-cancel-empty-pick>Cancel</button>
    </div>`;
}

export function emptyPickBannerMarkup(count) {
  return `<div class="dashboard-empty-pick-banner" role="status" data-empty-pick-banner>
      <span class="dashboard-empty-pick-banner__text">Pick the empty this driver takes back &mdash; showing ${count} Container${count === 1 ? '' : 's'} reported empty.</span>
      <button type="button" class="dashboard-empty-pick-banner__cancel" data-cancel-empty-pick>Cancel</button>
    </div>`;
}
